import { Phone, MessageCircle, Cpu, Disc3, Truck, ClipboardCheck, ArrowRight, MapPin, Star, Shield, Clock, ChevronRight } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import heroImage from "@/assets/hero-mechanic.jpg";
import diagnosticsImg from "@/assets/diagnostics.jpg";
import brakesImg from "@/assets/brakes.jpg";
import workshopImg from "@/assets/workshop.png";
import engineBayImg from "@/assets/engine-bay.png";
import logoImg from "@/assets/logo.png";

const quickServices = [
  { icon: Cpu, title: "Engine Diagnostics", desc: "OBD-II fault scanning & live data analysis", color: "from-primary/20 to-primary/5" },
  { icon: Disc3, title: "Brake & Suspension", desc: "Pads, discs, shocks & precision alignment", color: "from-orange-glow/20 to-orange-glow/5" },
  { icon: Truck, title: "Roadside Recovery", desc: "30-minute rapid response in Thika area", color: "from-primary/20 to-primary/5" },
  { icon: ClipboardCheck, title: "Pre-Purchase Check", desc: "150-point vehicle engineering inspection", color: "from-orange-glow/20 to-orange-glow/5" },
];

const stats = [
  { value: "500+", label: "Vehicles Serviced" },
  { value: "30min", label: "Avg Response Time" },
  { value: "150pt", label: "Inspection Points" },
  { value: "4.9★", label: "Customer Rating" },
];

const areas = ["Thika Town", "Section 9", "Landless", "Nanyuki Road", "Juja"];

const Home = () => {
  return (
    <>
      {/* Hero */}
      <section className="relative min-h-[100svh] flex items-center overflow-hidden">
        <div className="absolute inset-0">
          <img src={heroImage} alt="Professional mobile mechanic using diagnostic tools" className="w-full h-full object-cover" loading="eager" />
          <div className="absolute inset-0 bg-hero-overlay" />
          {/* Subtle animated grain */}
          <div className="absolute inset-0 noise-overlay" />
        </div>

        {/* Floating decorative orbs */}
        <div className="absolute top-1/4 right-10 w-64 h-64 bg-primary/8 rounded-full blur-[100px] animate-float" />
        <div className="absolute bottom-1/3 left-5 w-48 h-48 bg-orange-glow/6 rounded-full blur-[80px] animate-float" style={{ animationDelay: "1.5s" }} />

        <div className="relative z-10 container mx-auto px-4 py-12 sm:py-20">
          <div className="max-w-2xl">
            {/* Badge */}
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full glass-card-strong mb-6 animate-fade-up" style={{ animationDelay: "0.05s" }}>
              <Shield className="h-3.5 w-3.5 text-primary" />
              <span className="font-heading uppercase tracking-[0.15em] text-primary text-[10px] sm:text-xs">Certified Automotive Engineers</span>
            </div>

            <h1 className="font-heading text-4xl sm:text-5xl md:text-6xl lg:text-7xl uppercase leading-[1.05] text-secondary-foreground mb-5 animate-fade-up" style={{ animationDelay: "0.15s" }}>
              Expert Engineering at{" "}
              <span className="text-gradient">Your Doorstep</span>
            </h1>
            <p className="text-base sm:text-lg md:text-xl text-secondary-foreground/80 mb-3 animate-fade-up font-body leading-relaxed" style={{ animationDelay: "0.25s" }}>
              Serving <strong className="text-secondary-foreground">Thika &amp; Surroundings</strong> — from Section 9 to Juja.
              Professional mobile auto repair by Patrick Kungu &amp; Meja Jenerali.
            </p>
            <p className="text-silver/60 text-xs sm:text-sm mb-8 animate-fade-up" style={{ animationDelay: "0.3s" }}>
              We come to you. No towing. No waiting rooms.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 animate-fade-up" style={{ animationDelay: "0.4s" }}>
              <Button variant="hero" size="xl" className="text-sm sm:text-base" asChild>
                <a href="tel:+254700000000">
                  <Phone className="h-4 w-4 sm:h-5 sm:w-5" />
                  Call Now
                </a>
              </Button>
              <Button variant="whatsapp" size="xl" className="text-sm sm:text-base" asChild>
                <a href="https://wa.me/254700000000?text=Hi%20Precision%20Mobile%20Auto%2C%20I%20need%20a%20service" target="_blank" rel="noopener noreferrer">
                  <MessageCircle className="h-4 w-4 sm:h-5 sm:w-5" />
                  Book via WhatsApp
                </a>
              </Button>
            </div>
          </div>
        </div>

        <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-background via-background/50 to-transparent" />
      </section>

      {/* Stats Bar */}
      <section className="relative -mt-12 z-20 px-4">
        <div className="container mx-auto max-w-4xl">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4">
            {stats.map((stat, i) => (
              <div key={stat.label} className="bg-card rounded-xl p-4 sm:p-5 text-center shadow-card-premium border border-border/50 hover:border-primary/30 transition-all duration-300 hover:-translate-y-1">
                <p className="font-heading text-xl sm:text-2xl md:text-3xl text-primary">{stat.value}</p>
                <p className="text-muted-foreground text-[10px] sm:text-xs uppercase tracking-wider mt-1">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Quick Services */}
      <section className="section-padding bg-background">
        <div className="container mx-auto">
          <div className="text-center mb-10 sm:mb-14">
            <p className="font-heading uppercase tracking-[0.3em] text-primary text-xs sm:text-sm mb-3">What We Do</p>
            <h2 className="font-heading text-2xl sm:text-3xl md:text-5xl uppercase text-foreground mb-4">
              Our Core Services
            </h2>
            <div className="premium-divider mx-auto max-w-[80px]" />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5">
            {quickServices.map((s, i) => (
              <div key={s.title} className="relative bg-card border border-border/50 rounded-xl p-5 sm:p-6 hover:border-primary/40 hover:shadow-elevated transition-all duration-500 group hover:-translate-y-1 overflow-hidden">
                {/* Subtle gradient bg */}
                <div className={`absolute inset-0 bg-gradient-to-br ${s.color} opacity-0 group-hover:opacity-100 transition-opacity duration-500 rounded-xl`} />
                <div className="relative z-10">
                  <div className="p-3 rounded-xl bg-gradient-to-br from-primary/15 to-primary/5 w-fit mb-4 group-hover:shadow-premium transition-all duration-500">
                    <s.icon className="h-6 w-6 text-primary" />
                  </div>
                  <h3 className="font-heading text-base sm:text-lg uppercase text-card-foreground mb-2">{s.title}</h3>
                  <p className="text-muted-foreground text-xs sm:text-sm leading-relaxed">{s.desc}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center mt-10">
            <Button variant="heroOutline" size="lg" asChild>
              <Link to="/services">
                View All Services <ArrowRight className="h-4 w-4 ml-1" />
              </Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Workshop Showcase */}
      <section className="relative overflow-hidden">
        <div className="grid grid-cols-1 md:grid-cols-2 min-h-[400px] md:min-h-[500px]">
          <div className="relative">
            <img src={workshopImg} alt="Our professional workshop" className="w-full h-full object-cover min-h-[280px]" loading="lazy" />
            <div className="absolute inset-0 bg-gradient-to-r from-transparent to-secondary/20 md:to-secondary/50" />
          </div>
          <div className="bg-charcoal-gradient flex items-center p-8 sm:p-12 lg:p-16 relative noise-overlay">
            <div className="absolute top-0 right-0 w-48 h-48 bg-primary/5 rounded-full blur-[80px]" />
            <div className="relative z-10">
              <p className="font-heading uppercase tracking-[0.3em] text-primary text-xs sm:text-sm mb-3">Our Workshop</p>
              <h2 className="font-heading text-2xl sm:text-3xl md:text-4xl uppercase text-secondary-foreground mb-4 leading-tight">
                Professional-Grade <span className="text-gradient">Equipment</span>
              </h2>
              <p className="text-silver/70 text-sm sm:text-base leading-relaxed mb-6">
                We bring the full workshop experience to your doorstep with industry-standard diagnostic tools, precision instruments, and genuine parts.
              </p>
              <Button variant="hero" size="lg" asChild>
                <Link to="/about">
                  Learn More <ChevronRight className="h-4 w-4 ml-1" />
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Location Banner */}
      <section className="relative bg-gradient-to-r from-primary via-orange-glow to-primary py-8 sm:py-10 px-4 overflow-hidden">
        <div className="absolute inset-0 noise-overlay" />
        <div className="container mx-auto text-center relative z-10">
          <div className="flex items-center justify-center gap-2 mb-4">
            <MapPin className="h-4 w-4 sm:h-5 sm:w-5 text-primary-foreground" />
            <h3 className="font-heading text-lg sm:text-xl uppercase text-primary-foreground tracking-wider">
              Service Areas
            </h3>
          </div>
          <div className="flex flex-wrap justify-center gap-2 sm:gap-3">
            {areas.map((area) => (
              <span
                key={area}
                className="px-4 sm:px-5 py-1.5 sm:py-2 rounded-full bg-primary-foreground/15 text-primary-foreground text-xs sm:text-sm font-medium backdrop-blur-sm border border-primary-foreground/10 hover:bg-primary-foreground/25 transition-colors duration-300"
              >
                {area}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* Social Proof */}
      <section className="section-padding bg-card relative overflow-hidden">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-96 h-96 bg-primary/3 rounded-full blur-[120px]" />
        <div className="container mx-auto text-center relative z-10">
          <div className="max-w-lg mx-auto">
            <div className="flex items-center justify-center gap-1 mb-4">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className="h-5 w-5 sm:h-6 sm:w-6 fill-primary text-primary drop-shadow-sm" />
              ))}
            </div>
            <p className="font-heading text-xl sm:text-2xl md:text-3xl uppercase text-foreground mb-3">Trusted by Thika Drivers</p>
            <div className="premium-divider mx-auto max-w-[60px] mb-6" />
            <p className="text-muted-foreground text-sm sm:text-base leading-relaxed mb-8 italic">
              "Patrick fixed my car in Juja while I was at work! Professional service with a proper diagnostic report. I couldn't believe the quality."
            </p>
            <Button variant="heroOutline" size="lg" asChild>
              <Link to="/testimonials">
                Read More Reviews <ArrowRight className="h-4 w-4 ml-1" />
              </Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Preview Gallery */}
      <section className="section-padding bg-background">
        <div className="container mx-auto">
          <div className="text-center mb-10">
            <p className="font-heading uppercase tracking-[0.3em] text-primary text-xs sm:text-sm mb-3">Our Work</p>
            <h2 className="font-heading text-2xl sm:text-3xl md:text-4xl uppercase text-foreground mb-4">Recent Jobs</h2>
            <div className="premium-divider mx-auto max-w-[80px]" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4 max-w-4xl mx-auto">
            {[
              { src: diagnosticsImg, alt: "Engine diagnostics", label: "Diagnostics" },
              { src: engineBayImg, alt: "Engine bay service", label: "Engine Work" },
              { src: brakesImg, alt: "Brake service", label: "Brakes" },
            ].map((img, i) => (
              <div key={i} className="aspect-[4/3] overflow-hidden rounded-xl group relative shadow-card-premium">
                <img src={img.src} alt={img.alt} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" loading="lazy" />
                <div className="absolute inset-0 bg-gradient-to-t from-secondary/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                <span className="absolute bottom-3 left-3 font-heading text-xs uppercase tracking-wider text-primary-foreground opacity-0 group-hover:opacity-100 transition-all duration-500 translate-y-2 group-hover:translate-y-0">
                  {img.label}
                </span>
              </div>
            ))}
          </div>
          <div className="text-center mt-10">
            <Button variant="heroOutline" size="lg" asChild>
              <Link to="/gallery">
                View Full Gallery <ArrowRight className="h-4 w-4 ml-1" />
              </Link>
            </Button>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative bg-charcoal-gradient section-padding overflow-hidden noise-overlay">
        <div className="absolute top-0 right-0 w-80 h-80 bg-primary/8 rounded-full blur-[120px]" />
        <div className="absolute bottom-0 left-0 w-60 h-60 bg-orange-glow/5 rounded-full blur-[100px]" />
        <div className="container mx-auto text-center relative z-10">
          <img src={logoImg} alt="Precision Mobile Auto" className="h-16 sm:h-20 w-auto mx-auto mb-6 drop-shadow-lg" />
          <h2 className="font-heading text-2xl sm:text-3xl md:text-4xl lg:text-5xl uppercase text-secondary-foreground mb-4">
            Ready to Get <span className="text-gradient">Started?</span>
          </h2>
          <p className="text-silver/70 text-sm sm:text-base max-w-md mx-auto mb-8">
            Book your service today. We'll come to you anywhere in Thika and surrounding areas.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center">
            <Button variant="hero" size="xl" asChild>
              <a href="tel:+254700000000">
                <Phone className="h-5 w-5" />
                Call Now
              </a>
            </Button>
            <Button variant="whatsapp" size="xl" asChild>
              <a href="https://wa.me/254700000000?text=Hi%20Precision%20Mobile%20Auto%2C%20I%20need%20a%20service" target="_blank" rel="noopener noreferrer">
                <MessageCircle className="h-5 w-5" />
                WhatsApp Us
              </a>
            </Button>
          </div>
        </div>
      </section>
    </>
  );
};

export default Home;
